import { Translate } from '@change-corgi/core/react/i18n';
import { FakeLink } from '@change-corgi/design-system-legacy/components/actions';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { useIsSunriseTheme } from '@change-corgi/design-system-legacy/theme';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { SupporterComment } from '../../../../shared/types';

import { useIsCollapsed } from './hooks/useIsCollapsed';
import { useIsCommentTooLong } from './hooks/useIsCommentTooLong';

export function SupporterCommentCardBody({
	supporterComment,
}: {
	supporterComment: SupporterComment;
}): React.JSX.Element {
	const { isCommentTooLong, content } = useIsCommentTooLong(supporterComment);
	const {
		data: { isCollapsed },
		actions: { expand },
	} = useIsCollapsed();
	const isSunriseThemeEnabled = useIsSunriseTheme();

	const comment =
		isCommentTooLong && isCollapsed
			? content // truncated
			: supporterComment.comment;

	return isSunriseThemeEnabled ? (
		<SupporterCommentCardBodySunrise
			commentId={supporterComment.id}
			comment={comment}
			isCommentTooLong={isCommentTooLong}
			isCollapsed={isCollapsed}
			expand={expand}
		/>
	) : (
		<SupporterCommentCardBodyLegacy
			commentId={supporterComment.id}
			comment={comment}
			isCommentTooLong={isCommentTooLong}
			isCollapsed={isCollapsed}
			expand={expand}
		/>
	);
}

const COLLAPSED_STYLE = {
	maxHeight: '140px',
	overflow: 'hidden',
	backgroundImage: 'linear-gradient(black 75%, white)',
	color: 'transparent',
	backgroundClip: 'text',
};
const EXPANDED_STYLE = {
	height: 'auto',
};

export function SupporterCommentCardBodyLegacy({
	commentId,
	comment,
	isCommentTooLong,
	isCollapsed,
	expand,
}: {
	commentId: string;
	comment: string;
	isCommentTooLong: boolean;
	isCollapsed: boolean;
	expand: () => void;
}): React.JSX.Element {
	return (
		<Flex sx={{ gap: '15px' }}>
			<Box sx={{ backgroundColor: 'primary-changeRed', minWidth: '3px' }} />
			<Box>
				<Box
					data-testid={`supporter-comment-body-${commentId}`}
					sx={isCommentTooLong && isCollapsed ? COLLAPSED_STYLE : EXPANDED_STYLE}
				>
					<Text as="q" sx={{ fontStyle: 'italic' }}>
						{comment}
					</Text>
				</Box>
				{isCommentTooLong && isCollapsed && (
					<FakeLink
						onClick={expand}
						data-testid="show-full-text"
						data-qa="show-full-text"
						sx={{
							display: 'inline-block',
							color: 'black',
						}}
					>
						<Translate value="corgi.petition_gamma.details.description_show_full_text" />
					</FakeLink>
				)}
			</Box>
		</Flex>
	);
}
export function SupporterCommentCardBodySunrise({
	commentId,
	comment,
	isCommentTooLong,
	isCollapsed,
	expand,
}: {
	commentId: string;
	comment: string;
	isCommentTooLong: boolean;
	isCollapsed: boolean;
	expand: () => void;
}): React.JSX.Element {
	return (
		<Flex
			sx={{
				flexDirection: 'column',
				alignItems: 'flex-start',
				gap: '8px',
			}}
		>
			<Box data-testid={`supporter-comment-body-${commentId}`}>
				<Text
					as="q"
					sx={{
						fontFamily: 'serif',
						fontStyle: 'italic',
						lineHeight: '28px',
						wordBreak: 'break-word',
					}}
				>
					{comment}
				</Text>
			</Box>

			{isCommentTooLong && isCollapsed && (
				<FakeLink
					onClick={expand}
					data-testid="show-full-text"
					data-qa="show-full-text"
					sx={{
						display: 'inline-block',
						color: 'black',
					}}
				>
					<Translate value="corgi.petition_gamma.details.description_show_full_text" />
				</FakeLink>
			)}
		</Flex>
	);
}
